import { ArrowRight, Leaf, Sparkles } from "lucide-react";
import { BRAND, PRODUCTS } from "../../lib/brand";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 lg:pt-36 lg:pb-24 kraft-grain"
    >
      {/* Ambient spice-colored glows */}
      <div className="absolute -top-24 -right-20 w-[30rem] h-[30rem] rounded-full bg-[#D99026]/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[32rem] h-[32rem] rounded-full bg-[#B33922]/18 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[38rem] h-[38rem] rounded-full bg-[#2A4B3C]/8 blur-3xl pointer-events-none" />

      {/* Decorative corner chilies/leaves */}
      <DecorChili className="hidden lg:block absolute top-28 left-6 w-14 text-[#B33922] rotate-[-18deg] opacity-70" />
      <DecorLeaf className="hidden lg:block absolute bottom-24 left-[42%] w-16 text-[#2A4B3C] rotate-[25deg] opacity-60" />
      <DecorStar className="hidden lg:block absolute top-36 right-[42%] w-10 text-[#8F2B17] opacity-70" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left — copy */}
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2A4B3C]/10 text-mo-green text-xs uppercase tracking-[0.22em] mb-6">
            <Leaf size={12} /> 100% Organic · Small-batch · Karnataka
          </div>

          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] text-mo-ink tracking-tight">
            Spices that remember
            <br />
            <span className="italic text-mo-terracotta">the hands</span> that
            <br />
            ground them.
          </h1>

          <p className="mt-8 max-w-xl text-base sm:text-lg text-mo-ink-soft leading-relaxed">
            Sun-dried on village courtyards. Hand-pounded in small tins. Sealed
            the same week. At <span className="font-medium text-mo-ink">Maati Organics</span>,
            every pinch carries the red earth it came from — nothing added, nothing taken.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#products"
              data-testid="hero-cta-explore"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-mo-terracotta text-mo-cream font-medium hover:bg-[#8F2B17] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              Explore our spices
              <ArrowRight size={18} />
            </a>
            <a
              href={BRAND.whatsapp.link}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-[#2C2520]/30 text-mo-ink font-medium hover:bg-mo-ink hover:text-mo-cream transition-all duration-300"
            >
              <Sparkles size={16} />
              Order on WhatsApp
            </a>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4">
            <Stat number="3" label="Heritage blends" />
            <div className="h-8 w-px bg-[#5C5248]/20 hidden sm:block" />
            <Stat number="100%" label="Organic & hand-ground" />
            <div className="h-8 w-px bg-[#5C5248]/20 hidden sm:block" />
            <Stat number="0" label="Preservatives · colours" />
          </div>
        </div>

        {/* Right — spice composition */}
        <div className="lg:col-span-5 relative min-h-[520px] flex items-center justify-center">
          {/* Big spice texture disc */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="w-[26rem] h-[26rem] max-w-full rounded-full shadow-2xl relative overflow-hidden border-[14px] border-[#EAE3D9]"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1723155182094-af2f63472d0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzcGljZXMlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NjUyMzYzNXww&ixlib=rb-4.1.0&q=85')",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-[#8F2B17]/45 via-transparent to-[#D99026]/25" />
            </div>
          </div>

          {/* Center logo badge floating over */}
          <div className="relative z-10 w-48 h-48 sm:w-56 sm:h-56 rounded-full bg-mo-cream shadow-2xl flex items-center justify-center p-5 animate-float-slow ring-1 ring-[#5C5248]/10 overflow-hidden">
            <img
              src={BRAND.logo}
              alt="Maati Organics"
              className="w-full h-full object-contain rounded-full"
              data-testid="hero-logo"
            />
          </div>

          {/* Floating spice swatches around */}
          <SpiceChip
            className="absolute top-6 left-2 sm:left-0 animate-float"
            color="#D99026"
            label="haldi"
            hint="golden · earthy"
          />
          <SpiceChip
            className="absolute top-24 right-0 animate-float-slow"
            color="#B33922"
            label="lāl mirch"
            hint="bold · sun-dried"
          />
          <SpiceChip
            className="absolute bottom-20 left-0 animate-float-slow"
            color="#8F2B17"
            label="masala"
            hint="11-spice blend"
          />
          <div className="absolute bottom-2 right-2 sm:right-4 bg-mo-cream border border-[#5C5248]/15 rounded-full px-4 py-2 shadow-lg flex items-center gap-2 animate-float">
            <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse" />
            <span className="text-xs font-medium text-mo-ink">Fresh 2026 harvest · in</span>
          </div>

          {/* Mini product pouches peeking */}
          <div className="hidden md:flex absolute -bottom-4 left-1/2 -translate-x-1/2 gap-2 z-20">
            {PRODUCTS.map((p) => (
              <div
                key={p.slug}
                className="w-10 h-14 rounded-sm kraft-pouch overflow-hidden p-0.5"
                title={p.name}
              >
                <img
                  src={p.label}
                  alt={p.name}
                  className="w-full h-full object-contain bg-[#FAF6EE] rounded-sm"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ number, label }) => (
  <div>
    <div className="font-serif-display text-3xl text-mo-ink leading-none">{number}</div>
    <div className="text-xs uppercase tracking-[0.18em] text-mo-ink-soft mt-1">{label}</div>
  </div>
);

const SpiceChip = ({ color, label, hint, className = "" }) => (
  <div
    className={`bg-mo-cream border border-[#5C5248]/15 rounded-full pl-2 pr-4 py-1.5 shadow-lg flex items-center gap-2 ${className}`}
  >
    <span
      className="w-6 h-6 rounded-full ring-2 ring-white"
      style={{ backgroundColor: color, boxShadow: `inset 0 -4px 8px rgba(0,0,0,0.25)` }}
    />
    <div className="leading-tight">
      <div className="font-serif-display italic text-sm text-mo-ink">{label}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-mo-ink-soft">{hint}</div>
    </div>
  </div>
);

// Inline SVG decorations
const DecorChili = ({ className = "" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M22 6c-3 3-2 7 0 9 2 2 6 2 8 5 3 5 0 12-4 17-6 8-14 14-18 20" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M30 8c1-1 4-3 8-3s6 3 6 6c0 6-6 10-12 14-8 5-16 14-22 24" fill="currentColor" fillOpacity="0.85"/>
    <path d="M24 4c0 3 2 5 4 5s4-2 4-5" stroke="#2A4B3C" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const DecorLeaf = ({ className = "" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M8 56C8 30 28 8 56 8c0 26-22 48-48 48z" fill="currentColor" fillOpacity="0.85"/>
    <path d="M8 56C20 44 34 32 52 14" stroke="#F9F6F0" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const DecorStar = ({ className = "" }) => (
  <svg viewBox="0 0 64 64" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M32 4l4 18 18 4-18 4-4 18-4-18-18-4 18-4z" opacity="0.9"/>
  </svg>
);
